import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { appRoutes } from './app.routes';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { authInterceptor, httpErrorInterceptor, provideAuth } from '@angular-monorepo/auth';
import { devAuthInterceptor } from './dev-auth.interceptor';
import { API_URL, GlobalErrorHandler, MS_GRAPH_CONFIG } from '@angular-monorepo/data-access';
import { MessageService } from 'primeng/api';
import { environment } from '../environments/environment';
import { ConfigService } from '../environments/environment.service';


export function createTranslateLoader(http: HttpClient): TranslateLoader {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

const DitrackPreset = definePreset(Aura, {
  semantic: {
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '{zinc.50}',
          100: '{zinc.100}',
          200: '{zinc.200}',
          300: '{zinc.300}',
          400: '{zinc.400}',
          500: '{zinc.500}',
          600: '{zinc.600}',
          700: '{zinc.700}',
          800: '{zinc.800}',
          900: '{zinc.900}',
          950: '{zinc.950}',
        },
      },
      dark: {
        surface: {
          0: '#ffffff',
          50: '#f0f4f8',
          100: '#d9e2ec',
          200: '#bcccdc',
          300: '#9fb3c8',
          400: '#829ab1',
          500: '#627d98',
          600: '#486581',
          700: '#334e68',
          800: '#243b53',
          900: '#1a2d47',
          950: '#0f1c2e',
        },
      },
    },
    primary: {
      50: '#eef0f6',
      100: '#d5d8e5',
      200: '#b8bdd1',
      300: '#9aa1bd',
      400: '#7d85a9',
      500: '#616a95',
      600: '#515880',
      700: '#414769',
      800: '#333856',
      900: '#262a43',
      950: '#191c30',
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes, withEnabledBlockingInitialNavigation()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: DitrackPreset,
        options: {
          prefix: 'p',
          darkModeSelector: '.dark',
          cssLayer: false,
        },
      },
    }),
    provideAuth(),
    provideHttpClient(withInterceptors([devAuthInterceptor, authInterceptor, httpErrorInterceptor])),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    MessageService,
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      })
    ),
    {
      provide: 'AUTH_CONFIG',
      useValue: {
        apiUrl: environment.apiUrl,
        loginEndpoint: '/api/auth/login/',
      },
    },
    {
      provide: API_URL,
      useFactory: (configService: ConfigService) => configService.getApiUrl(),
      deps: [ConfigService],
    },
    {
      provide: MS_GRAPH_CONFIG,
      useValue: {
        clientId: environment.microsoftGraph.clientId,
        tenantId: environment.microsoftGraph.tenantId,
        redirectUri: environment.microsoftGraph.redirectUri,
        scopes: environment.microsoftGraph.scopes,
      },
    },
  ],
};